import React, { FC, useEffect, useRef, useState } from "react";
import { Card, Collapse, FormControl } from "react-bootstrap";
import { MutatorProperty } from "../../../models/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { useMutatorState } from "../../../graphql/hooks";
import styles from "./CustomTypeCard.module.scss";
import { useTypeCardVariant } from "./leftBorderStyle";
import cx from "classnames";

interface Props {
    property: MutatorProperty;
    getVariantByType: (type: string) => string;
    onChange: (name: string, value: number) => void;
}

const MutatorNumberCardImplementation: FC<Props> = ({
    property,
    getVariantByType,
    onChange
}) => {
    const inputRef = useRef(null);
    const mutatorState = useMutatorState();
    const { cardClassName, renderTypeBadge } = useTypeCardVariant(
        property.type,
        getVariantByType
    );
    const [value, updateValue] = useState(property.value);
    const [showMeta, updateShowMeta] = useState(mutatorState.showMeta);
    const [notification, updateNotification] = useState<string | undefined>(
        undefined
    );

    useEffect(() => {
        if (showMeta !== mutatorState.showMeta) {
            updateShowMeta(mutatorState.showMeta);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mutatorState.showMeta]);

    useEffect(() => {
        validateMeta(property.value);
    }, [property.value]);

    const forceClickBehaviour = () => {
        // Some browers do not focus the form properly when clicking increase / decrease
        if (inputRef.current) {
            // @ts-ignore
            inputRef.current.focus();
        }
    };

    const validateMeta = (valueOld: any) => {
        let newValue = Number(valueOld);
        const trimmedInput = valueOld?.toString().trim();
        if (
            // @ts-ignore
            !(
                !isNaN(parseFloat(trimmedInput)) &&
                // @ts-ignore
                isFinite(trimmedInput)
            )
        ) {
            updateNotification("Valid number is required");
            updateValue(valueOld);
            return;
        } else if (
            property.meta?.min !== undefined &&
            newValue < property.meta.min
        ) {
            updateNotification(`Minimum allowed value ${property.meta.min}`);
            newValue = property.meta.min;
        } else if (
            property.meta?.max !== undefined &&
            newValue > property.meta.max
        ) {
            updateNotification(`Maximum allowed value ${property.meta.max}`);
            newValue = property.meta.max;
        } else {
            updateNotification(undefined);
        }
        updateValue(newValue);
    };

    return (
        <Card className={`inner-card text-contrast m-2 ${cardClassName}`}>
            <Card.Header
                className={`d-flex align-items-center ${styles.cardHeader}`}
            >
                <span className={styles.headerText}>{property.name}</span>
                {renderTypeBadge()}
                {property.meta ? (
                    <div
                        className="float-end ms-auto mouseHover"
                        onClick={() => updateShowMeta(!showMeta)}
                        title="Help and additional info"
                    >
                        <FontAwesomeIcon
                            icon={faCircleInfo}
                            className={cx("ms-2", {
                                "text-disabled": !showMeta,
                                "text-muted": showMeta
                            })}
                        />
                    </div>
                ) : null}
            </Card.Header>
            <div>
                <Card.Body className={`${styles.cardBody} ms-1`}>
                    <FormControl
                        ref={inputRef}
                        className={styles.inputGroup}
                        type="number"
                        placeholder="Add value"
                        value={value}
                        onClick={forceClickBehaviour}
                        onChange={e => {
                            validateMeta(e.target.value);
                        }}
                        onBlur={e => {
                            validateMeta(e.target.value);
                            onChange(property.name, value);
                        }}
                    />
                </Card.Body>
            </div>
            {property.meta ? (
                <Collapse in={showMeta}>
                    <div>
                        <Card.Footer className={styles.footerInfo}>
                            {property.meta.description ? (
                                <div className="bread-text text-subtle">
                                    {property.meta.description}
                                </div>
                            ) : (
                                <span className="bread-text text-subtle">
                                    No description provided.
                                </span>
                            )}
                            {property.meta.min !== undefined ||
                            property.meta.max !== undefined ? (
                                <div className="bread-text text-info">
                                    {property.meta.min !== undefined ? (
                                        <div>Min: {property.meta.min} </div>
                                    ) : null}
                                    {property.meta.max !== undefined ? (
                                        <div>Max: {property.meta.max} </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </Card.Footer>
                    </div>
                </Collapse>
            ) : null}
            {notification ? (
                <Card.Footer className={styles.footerInfo}>
                    <div className="bread-text text-danger">{notification}</div>
                </Card.Footer>
            ) : null}
        </Card>
    );
};

export const MutatorNumberCard = React.memo(MutatorNumberCardImplementation);
